import { useState } from 'react'
import {
  type AdminRole,
  roleHasUsers,
  roleIdFromSlug,
  useAdminRoles,
  useCreateRole,
  useDeleteRole,
  useUpdateRole,
} from '../../../features/admin/use-admin-roles'
import { AdminPage, adminError, td } from './admin-bits'
import { Btn, ConfirmDialog, Dialog, Field, Inline, input, th } from './controls'
import { mono, Note, Section, serif } from './ui'

function RoleDialog({ role, onClose }: { role: AdminRole | null; onClose: () => void }) {
  const create = useCreateRole()
  const update = useUpdateRole()
  const [name, setName] = useState(role?.name ?? '')
  const [desc, setDesc] = useState(role?.description ?? '')
  const m = role ? update : create
  const submit = () => {
    const opts = { onSuccess: onClose }
    if (role) update.mutate({ roleId: role.id, name: name.trim(), description: desc.trim() }, opts)
    else create.mutate({ id: roleIdFromSlug(name), name: name.trim(), description: desc.trim() || undefined }, opts)
  }
  return (
    <Dialog
      open
      eyebrow={role ? 'Amend' : 'New entry'}
      title={role ? 'Edit role' : 'Create a role'}
      onClose={onClose}
      onSubmit={() => name.trim() !== '' && !m.isPending && submit()}
      footer={
        <>
          <Btn onClick={onClose} disabled={m.isPending}>
            Cancel
          </Btn>
          <Btn variant="primary" onClick={submit} disabled={name.trim() === '' || m.isPending}>
            {m.isPending ? 'Saving…' : role ? 'Save' : 'Create role'}
          </Btn>
        </>
      }
    >
      <Field label="Name">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      {role ? null : (
        <p className="text-[12px] text-[var(--color-muted)]">
          Role id: <span style={mono}>{roleIdFromSlug(name)}</span>
        </p>
      )}
      <Field label="Description">
        <input className={input} value={desc} onChange={(e) => setDesc(e.target.value)} />
      </Field>
      {m.error ? <Inline tone="bad">{adminError(m.error)}</Inline> : null}
    </Dialog>
  )
}

export function LedgerAdminRoles() {
  const roles = useAdminRoles()
  const del = useDeleteRole()
  const [edit, setEdit] = useState<AdminRole | 'new' | null>(null)
  const [remove, setRemove] = useState<AdminRole | null>(null)
  const list = roles.data ?? []
  return (
    <AdminPage
      title="Roles"
      standfirst="The roles this organization hands out. A role with members cannot be deleted until they are reassigned."
      loading={roles.isLoading}
      error={!!roles.error}
      aside={
        <Btn variant="primary" onClick={() => setEdit('new')}>
          Create role
        </Btn>
      }
    >
      <Section title={`${list.length} ${list.length === 1 ? 'role' : 'roles'}`}>
        {list.length === 0 ? (
          <Note>No roles yet.</Note>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className={th}>Name</th>
                <th className={th}>Id</th>
                <th className={th}>Description</th>
                <th className={th}>Members</th>
                <th className={`${th} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td className={`${td} text-[16px] font-semibold`} style={serif}>
                    {r.name}
                  </td>
                  <td className={`${td} text-[12.5px]`} style={mono}>
                    {r.id}
                  </td>
                  <td className={`${td} text-[var(--color-muted)]`}>{r.description ?? '-'}</td>
                  <td className={`${td} tabular-nums`}>{r.userCount}</td>
                  <td className={`${td} text-right`}>
                    <Btn onClick={() => setEdit(r)}>Edit</Btn>{' '}
                    <Btn
                      variant="danger"
                      disabled={roleHasUsers(r)}
                      title={roleHasUsers(r) ? 'Reassign its users first' : undefined}
                      onClick={() => setRemove(r)}
                    >
                      Delete
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
      {edit ? <RoleDialog role={edit === 'new' ? null : edit} onClose={() => setEdit(null)} /> : null}
      <ConfirmDialog
        open={remove !== null}
        title="Delete this role?"
        body={<>The role {remove?.name} and its permission grants will be removed.</>}
        confirmLabel="Delete"
        danger
        pending={del.isPending}
        error={del.error ? adminError(del.error) : null}
        onConfirm={() => remove && del.mutate({ roleId: remove.id }, { onSuccess: () => setRemove(null) })}
        onClose={() => {
          del.reset()
          setRemove(null)
        }}
      />
    </AdminPage>
  )
}
